import { debounce } from "lodash";
import { useEffect, useState, Fragment } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { BiRupee } from "react-icons/bi";
import { toast } from "react-hot-toast";
import { Clock } from "lucide-react";

import { ICartSearchResponseData, IHDCartAddressListResponseData } from "@/api_framework/api_modals/Cart";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useCartSearchAPI } from "@/app/hooks/api_hooks/Cart/useCartSearchAPI";
import { useLoader } from "@/app/hooks/useLoader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

const UnifiedCartProductSearchComponent: React.FC<{
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  onSelectItem?: (item: ICartSearchResponseData) => void;
  currentAddress?: IHDCartAddressListResponseData | null;
  isOnlyServiceItem?: boolean;
  showStatusTags?: boolean;
    clinicId?: string;
}> = ({
  containerClassName,
  inputContainerClassName,
  onSelectItem,
  currentAddress,
  isOnlyServiceItem = false,
  showStatusTags = true,
  clinicId,
  ...props
}) => {
  const { getRouteKey } = useRouter();
  const { data: searchResults, execute: searchCartProducts, clearSearchResults } = useCartSearchAPI();
  const [selected, setSelected] = useState<ICartSearchResponseData | null>(null);
  const [isBarcodeScan, setIsBarcodeScan] = useState(false);
  const [lastInputTime, setLastInputTime] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const debouncedQuery = useDebounce(query?.trim(), 300);
  const isSearchPending = useLoader("isCustomerCartItemsSearchInProgress");

  const tabs = ["All", "Food & Accessories", "Medicines", "Services", "Membership", "Lab"];
  const selectedTab = isOnlyServiceItem ? "Services" : activeTab;

  const { execute: logAPI } = useClickLogAPI();
  const mappedClinicID = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const customerProfileState = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"))?.routeState?.state as
    | ICustomerProfileState
    | undefined;
  const petId = customerProfileState?.petId;
  const customerId = customerProfileState?.customerId;
  const cartState = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))?.routeState?.state as ICartState | undefined;
  const activeCartDetails = cartState?.activeCartDetails;
  const isUrgentCancel = !!activeCartDetails?.is_urgent_cancel;
  const activeCartId = cartState?.activeCartId;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const effectiveClinicId = clinicId ?? mappedClinicID;

  const getCategoryForAPI = (tab: string) => {
    switch (tab) {
      case "Food & Accessories":
        return "Food";
      case "Medicines":
        return "Medicines";
      case "Services":
        return "Services";
      case "Membership":
        return "Membership";
      case "Lab":
        return "Lab";
      default:
        return "All";
    }
  };

  const callLogAPI = (searchString: string, searchData?: any, clickItem?: any) => {
    const logBody: IClickLogBody = {
      event_type: clickItem ? "CART_CLICK_BOOKING" : searchData ? "CART_VIEW_BOOKING" : "CART_SEARCH_BOOKING",
      scr_id: "CART",
      client_id: customerId,
      emr_id: activeCartDetails?.emr_id,
      cart_id: activeCartId,
      pet_id: petId,
      search_item: searchString,
      search_tag: selectedTab,
      search_result: searchData ?? undefined,
      click_on_search_item: clickItem ?? undefined,
      clinic_id: effectiveClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const debounceSearch = debounce(() => {
    if (!debouncedQuery || debouncedQuery.length < 3) return;
    searchCartProducts(
      debouncedQuery,
      effectiveClinicId,
      petId,
      isOnlyServiceItem ? ["Services", "Lab"] : getCategoryForAPI(selectedTab),
      currentAddress?.pincode,
      activeCartId
    ).then(({ status, message }) => {
      callLogAPI(debouncedQuery, status ? message : []);
    });
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) debounceSearch();
    else clearSearchResults();
  }, [debouncedQuery, selectedTab, effectiveClinicId]);

  // Address change invalidates results fetched for the previous pincode
  useEffect(() => {
    clearSearchResults();
    if (debouncedQuery.length >= 3) debounceSearch();
  }, [currentAddress?.pincode]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (lastInputTime && now - lastInputTime < 50) {
      setIsBarcodeScan(true);
    } else {
      setIsBarcodeScan(false);
    }
    setLastInputTime(now);
    if (event.key === "Enter" && query.length >= 6) {
      setIsBarcodeScan(true);
    }
  };
  useEffect(() => {
    if (isBarcodeScan && searchResults?.length === 1) {
      onSelectItem?.(searchResults[0]);
      setQuery("");
      clearSearchResults();
    }
  }, [isBarcodeScan, searchResults]);

  useEffect(() => {
    if (query === "" && !isOnlyServiceItem) setActiveTab("All");
  }, [query, isOnlyServiceItem]);

  return (
    <div className={clsx(containerClassName, isUrgentCancel && "opacity-60 cursor-not-allowed")}>
      <Combobox
        disabled={isUrgentCancel}
        value={selected}
        onChange={(item) => {
          if (item && query.trim().length >= 3) {
            setSelected(item);
            onSelectItem?.(item);
            callLogAPI(debouncedQuery, null, item);
          } else if (query.trim().length < 3) {
            toast.error("Please type at least 3 characters to add item.");
          }
        }}
      >
        <div className="relative mt-1 w-full">
          {/* Input Box */}
          <div className="relative border-gray-200 bg-white border rounded-lg focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full text-left sm:text-sm overflow-hidden focus:outline-none">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  type="button"
                  className="p-2"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setQuery("");
                    clearSearchResults();
                    setIsBarcodeScan(false);
                  }}
                >
                  <XMarkIcon className="w-4 h-4 text-red-500 cursor-pointer hover:text-red-800" />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  autoFocus
                  value={query}
                  placeholder={props?.placeholder ?? "Start Searching ..."}
                  className="py-2 pr-10 pl-3 border-none focus:ring-0 w-full text-gray-900 text-sm leading-5"
                  onChange={(e) => setQuery(e.target.value)}
                  onBlur={() => onSelectItem?.(null as any)}
                  onKeyDown={handleKeyPress}
                />
              </Combobox.Button>
            </div>
            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>

          {/* Dropdown */}
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="z-20 absolute w-full bg-white shadow-lg mt-1 rounded-md ring-1 ring-black/5 max-h-96 auto text-base sm:text-sm focus:outline-none">
              <div className="max-h-72 overflow-y-auto">
                {/* Tabs */}
                {!isOnlyServiceItem && (
                  <div className="flex gap-2 m-3">
                    {tabs.map((tab) => (
                      <div
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`cursor-pointer text-xs rounded-2xl py-1 px-2 whitespace-nowrap border ${activeTab === tab
                            ? "bg-black text-white border-black"
                            : "bg-[#f4f4f4] text-[#4d4f53] border-[#e2e2e2]"
                          }`}
                      >
                        {tab}
                      </div>
                    ))}
                  </div>
                )}

                {/* Search Results */}
                {searchResults.length === 0 && query !== "" ? (
                  <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                    {isSearchPending ? <CircleSpinnerLoader h="20" w="20" /> : <p>No Result Found.</p>}
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-2 text-[#4d4f53]">
                      Search Results for <span className="text-[#0069f9] font-semibold">{debouncedQuery}</span>
                    </div>

                    {searchResults.map((result, idx) => (
                      <Combobox.Option
                        key={`${result?.id}:${idx}`}
                        className="relative cursor-default select-none p-4 text-gray-900 ml-1"
                        value={result}
                      >
                        {({ selected }) => (
                          <div className="flex justify-between items-center gap-x-2 w-full">
                            {/* Product Name */}
                            <div className="flex-1">
                              <span className={`block text-[13px] ${selected ? "font-medium" : "font-normal"}`}>
                                {result.name?.split("_").join(" ")}
                              </span>
                              {result.tat && (
                                <div className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 text-[10px] font-semibold border border-slate-100">
                                  <Clock className="w-3 h-3 text-slate-400" />
                                  <span>TAT: {result.tat} {Number(result.tat) === 1 ? "Hr" : "Hrs"}</span>
                                </div>
                              )}
                            </div>

                            {/* Price */}
                            {Number(result?.price) > 0 && (
                              <div className="flex items-center gap-1 w-[70px] justify-start mx-4">
                                <BiRupee className="text-[13px]" />
                                <span className="text-black text-[13px]">{result?.price}</span>
                              </div>
                            )}

                            {/* Stock Badge */}
                            {selectedTab !== "Services" && result?.stock_qty_with_text && (
                              <div className="flex gap-x-2 w-[110px] justify-end items-center">
                                {result?.stock_qty_with_text?.toLowerCase() === "unavailable" ? (
                                  <SimpleBadge
                                    colorClassName="text-[#b93b48] bg-[#fbdfe3] ring-[#b93b48] w-[110px] flex items-center justify-center"
                                    label="Unavailable"
                                  />
                                ) : (
                                  <div className="flex items-center gap-1">
                                    {result.is_hd_stock && <span className="text-red-600 text-[13px]">HD</span>}
                                    <SimpleBadge
                                      colorClassName={
                                        result?.is_hd_stock
                                          ? "text-[#006ecf] bg-[#edf5fe] ring-[#006ecf] w-[110px] flex items-center justify-center"
                                          : "text-[#01860A] bg-[#F1FDF4] ring-[#01860A] w-[110px] flex items-center justify-center"
                                      }
                                      label={result?.stock_qty_with_text}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </>
                )}
              </div>

              {/* Legend */}
              {showStatusTags && (
                <div className="flex gap-2 m-3">
                  <div className="bg-[#F1FDF4] text-[#01860A] rounded-md py-1 px-2 text-xs">Availability</div>
                  <div className="bg-[#edf5fe] text-[#0e6cd1] rounded-md py-1 px-2 text-xs">Home Delivery</div>
                  <div className="bg-[#fbdfe3] text-[#b93b48] rounded-md py-1 px-2 text-xs">Unavailable</div>
                </div>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default UnifiedCartProductSearchComponent;
